<template>
  <div>
    <span class="caption font-weight-medium" v-if="label">
      {{ label }}
      <span class="error--text" v-if="required">*</span>
    </span>
    <v-text-field
      v-model="model"
      :prepend-inner-icon="computedPrependIcon"
      :append-icon="appendIcon"
      :readonly="readonly"
      :disabled="disabled"
      :placeholder="placeholder"
      :persistent-hint="persistentHint"
      :suffix="suffix"
      :type="type"
      :hint="hint"
      :min="min"
      :max="max"
      :hide-details="hideDetails"
      :autocomplete="autocomplete"
      :rules="rules"
      dense
      :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
      solo
      flat
      filled
      @change="onChange"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
      @click:append="$emit('appendIconClick')"
      :name="
        preventPrefill && (type === 'email' || type === 'password')
          ? `fake-${type}-${Math.random()}`
          : undefined
      "
      @input="onInput"
    >
      <template v-slot:append v-if="append || clearable">
        <v-icon
          v-if="clearable && model"
          small
          color="error"
          @click="model = null"
          style="cursor: pointer"
          class="mt-1"
        >
          mdi-close-circle
        </v-icon>
        <slot name="append" v-if="append"></slot>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasModel],
  props: {
    label: {
      type: String,
      required: false,
      default: "",
    },
    kind: {
      type: String,
      required: false,
      default: "",
    },
    append: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    persistentHint: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    prependInnerIcon: {
      required: false,
      type: String,
      default: undefined,
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
    appendIcon: {
      required: false,
      type: String,
      default: undefined,
    },
    hint: {
      type: String,
      required: false,
      default: "",
    },
    autocomplete: {
      type: String,
      required: false,
      default: "off",
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    preventPrefill: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: "text",
    },
    suffix: {
      type: String,
      required: false,
      default: "",
    },
    min: {
      type: [String, Number],
      required: false,
      default: "",
    },
    max: {
      type: [String, Number],
      required: false,
      default: "",
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  created() {
    setTimeout(() => {
      if (this.type === "email" || this.type === "password") {
        this.model = "";
      }
    }, 1000);
  },
  mounted() {
    if (this.preventPrefill && (this.type === "email" || this.type === "password")) {
      // Set readonly temporarily to prevent autofill, then remove it
      this.$nextTick(() => {
        const input = this.$el.querySelector("input");
        if (input) {
          input.setAttribute("readonly", "readonly");
          setTimeout(() => {
            input.removeAttribute("readonly");
          }, 500);
        }
      });

      // Multiple attempts to clear autofilled values
      const clearValue = () => {
        if (this.model && this.model !== "") {
          this.model = "";
        }
      };

      setTimeout(clearValue, 100);
      setTimeout(clearValue, 300);
      setTimeout(clearValue, 500);
      setTimeout(clearValue, 1000);
      setTimeout(clearValue, 1500);
    }
  },
  computed: {
    computedPrependIcon() {
      // Ako je prependInnerIcon eksplicitno postavljen, koristi ga
      if (this.prependInnerIcon) {
        return this.prependInnerIcon;
      }

      // Pokušaj dodati ikonicu na osnovu kind-a
      if (this.kind) {
        switch (this.kind) {
          case "phone":
            return "mdi-phone-outline";
          case "email":
            return "mdi-email-outline";
          case "url":
          case "website":
            return "mdi-web";
          case "address":
          case "location":
            return "mdi-map-marker-outline";
          case "user":
          case "person":
            return "mdi-account-outline";
          case "company":
            return "mdi-domain";
          case "calendar":
          case "date":
            return "mdi-calendar-outline";
          case "time":
            return "mdi-clock-outline";
          case "search":
            return "mdi-magnify";
          case "money":
          case "currency":
            return "mdi-currency-usd";
          case "note":
          case "comment":
            return "mdi-note-text-outline";
          default:
            return undefined;
        }
      }

      return undefined;
    },
  },
  methods: {
    onInput(value) {
      // Za number type, validuj min i max odmah na input
      if (this.type === 'number') {
        let numValue = parseFloat(value);
        
        // Ako nije broj, postavi na min ili 0
        if (isNaN(numValue)) {
          numValue = this.min ? parseFloat(this.min) : 0;
        }
        
        // Proveri minimum
        if (this.min && numValue < parseFloat(this.min)) {
          numValue = parseFloat(this.min);
        }
        
        // Proveri maximum
        if (this.max && numValue > parseFloat(this.max)) {
          numValue = parseFloat(this.max);
          // Ažuriraj model direktno da se prikaže nova vrednost
          this.$nextTick(() => {
            this.model = numValue;
          });
        }
      }
    },
    onChange() {
      if (this.min) {
        this.model = Math.max(this.min, this.model);
      }
      this.$emit("change", this.model);
    },
  },
  watch: {
    type(type) {
      if (!this.preventPrefill && (this.type === "email" || this.type === "password")) {
        if (this.$route.name === "employees") {
          setTimeout(() => (this.model = ""), 1000);
        }
      }
    },
    model(newVal) {
      // Za number type, enforsiraj min/max ograničenja
      if (this.type === 'number') {
        let numValue = parseFloat(newVal);
        
        if (!isNaN(numValue)) {
          // Proveri min
          if (this.min && numValue < parseFloat(this.min)) {
            this.$nextTick(() => {
              this.model = parseFloat(this.min);
            });
            return;
          }
          
          // Proveri max
          if (this.max && numValue > parseFloat(this.max)) {
            this.$nextTick(() => {
              this.model = parseFloat(this.max);
            });
            return;
          }
        }
      }
    },
  },
};
</script>
